import React, { useContext, useEffect, useState } from "react";
import { StandardProps } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Delete, GetApp } from "@material-ui/icons";
import { tesaDarkRedError } from "../../../styles/colors";
import AttachmentApi from "../../../api/services/AttachmentApi";
import Attachment from "../../../api/models/Attachment";
import CustomTable, { CustomTableRowData } from "../../../components/CustomTable";
import useEnv from "../../../hooks/env";
import { dashToSlash } from "../../../api/services/common/FormatDateService";
import TestRequestApi from "../../../api/services/TestRequestApi";
import { HttpErrorType } from "../../../models/HttpErrorType";
import { ConfirmationDialogParams } from "../../../components/Dialog/ConfirmationDialog/ConfirmationDialog";
import TestRequestResponse from "../../../api/models/test-request/TestRequestResponse";
import TestRequestPermissionContext from "../../../contexts/TestRequestPermissionContext";

const useStyles = makeStyles({
  bold: {
    fontWeight: "bold",
  },
});

function convertAttachmentsToRows(attachments: Array<Attachment>): CustomTableRowData[] {
  const tableRows: CustomTableRowData[] = [];

  attachments.forEach((attachment, i) => {
    tableRows.push({
      name: attachment.name,
      created: dashToSlash(attachment.created.split("T")[0]),
      description: attachment.description,
      id: i,
      uuid: attachment.uuid,
    });
  });

  return tableRows;
}
export interface ReportOverviewProps extends StandardProps<React.HTMLAttributes<HTMLDivElement>, never, "children"> {
  testRequest:
    | (Omit<Required<TestRequestRaw.Schemas.TestRequestResponse>, "reportAttachments"> & {
        reportAttachments: Required<TestRequestRaw.Schemas.TestRequestAttachmentReferenceDTO>[];
      })
    | undefined;
  handleHttpError: (error: HttpErrorType | undefined) => void;
  showConfirmationDialog: (params?: ConfirmationDialogParams) => Promise<string>;
  updateTestRequest: (testRequest: TestRequestResponse) => void;
}

const ReportOverviewTable: React.FunctionComponent<ReportOverviewProps> = ({
  testRequest,
  handleHttpError,
  showConfirmationDialog,
  updateTestRequest,
}) => {
  const { api } = useEnv();
  const classes = useStyles();
  const attachmentApiService: AttachmentApi = new AttachmentApi(api);
  const testRequestApiService: TestRequestApi = new TestRequestApi(api);
  const [rows, setRows] = useState(convertAttachmentsToRows([]));
  const { deleteReport, downloadReport } = useContext(TestRequestPermissionContext);

  const purpose = testRequest?.purpose || "DEFAULT";
  const header = purpose === "DEFAULT" ? "Reports" : "Documents";
  const columnName = purpose === "DEFAULT" ? "Report Name" : "Document Name";

  const fetchAttachments = () => {
    if (testRequest?.reportAttachments && testRequest?.reportAttachments.length > 0) {
      const reportAttachmentUUIDs = testRequest.reportAttachments.map((ra) => ra.uuid);
      return testRequestApiService
        .getTestRequestAttachmentClearanceToken(testRequest.requestId)
        .then(({ data: token }) =>
          attachmentApiService
            .fetchAttachments({
              accessToken: token,
              attachmentIds: reportAttachmentUUIDs,
            })
            .then((data) => {
              setRows(convertAttachmentsToRows(data));
            })
            .catch(handleHttpError),
        );
    }
    setRows([]);
  };

  const handleDelete = (fileName: string, attachmentUuid: string) => {
    if (testRequest) {
      return showConfirmationDialog({
        confirmColor: tesaDarkRedError,
        text: (
          <>
            You are about to delete the file <span className={classes.bold}>{fileName}</span>.
          </>
        ),
      })
        .then(() => testRequestApiService.removeReportAttachmentFromRequest(testRequest.requestId, attachmentUuid))
        .then(({ data }) => updateTestRequest(data))
        .catch(handleHttpError);
    }
  };

  const handleDownload = (name: string, uuid: string) => {
    if (testRequest) {
      return testRequestApiService
        .getTestRequestAttachmentClearanceToken(testRequest.requestId)
        .then(({ data: token }) =>
          attachmentApiService.handleDownload(name, {
            accessToken: token,
            attachmentIds: [uuid],
          }),
        )
        .catch(handleHttpError);
    }
  };

  useEffect(() => {
    fetchAttachments();
  }, [testRequest]);

  return (
    <div hidden={rows.length === 0}>
      <CustomTable
        header={header}
        boldIndex={0}
        columns={[columnName, "Uploaded", "Description"]}
        columnMaxWidths={["25vw", "25vw", "25vw"]}
        actionColumnIndexes={[0]}
        excludedKeys={["id", "uuid"]}
        handleEdit={({ name, uuid }) => handleDownload(name, uuid)}
        hasRowNumbers
        formatDates
        rowMenus
        rowMenuActions={[
          {
            action: ({ name, uuid }) => handleDownload(name, uuid),
            show: () => Boolean(downloadReport),
            icon: <GetApp />,
            text: "Download",
          },
          {
            action: ({ name, uuid }) => handleDelete(name, uuid),
            show: () => Boolean(deleteReport),
            icon: <Delete />,
            text: "Delete",
          },
        ]}
        rows={rows}
      />
    </div>
  );
};

export default ReportOverviewTable;
